import * as THREE from 'three';
import EnemyHelicopter from './EnemyHelicopter.js';
import EnemyPlane from './EnemyPlane.js';
import EnemyGroundVehicle from './EnemyGroundVehicle.js';
import EnemyTunguska from './EnemyTunguska.js';
import GroundFireSystem from '../fx/GroundFireSystem.js';
import { HELICOPTERS, PLANES, VEHICLES, ENEMIES, ROADS } from '../core/SimConfig.js';
import { DEG2RAD, RAD2DEG, srand } from '../utils.js';

// Scratch for road-graph sampling during spawn placement (spawn-time only).
const _roadSample = { x: 0, z: 0, tx: 0, tz: 0 };

// Offset distances tried per side when parking a stationary vehicle off a road
// (_placeRoadside). Each costs a full-network nearest-point scan, but this runs
// once per AAA spawn at scenario load, never per frame.
const ROADSIDE_ATTEMPTS = 4;

/**
 * Owns and ticks all AI-controlled enemy entities for the active scenario.
 * Type tables (HELICOPTERS, PLANES, VEHICLES in SimConfig) drive routing.
 */
export default class EnemyManager {
  constructor({ scene, assets, terrainCollider, particleSystem, explosionSystem = null, audioManager, flareSystem = null, missileSystem = null, roadNetwork = null }) {
    this.scene = scene;
    this.assets = assets;
    this.terrainCollider = terrainCollider;
    this.particleSystem = particleSystem;
    this.explosionSystem = explosionSystem;
    this.audioManager = audioManager;
    this.flareSystem = flareSystem;
    this.missileSystem = missileSystem;
    this.roadNetwork = roadNetwork;
    this.entities = [];
    // Permanent fires left at enemy aircraft crash sites — the wreck itself is
    // pruned on impact, so the fire outlives it here.
    this.groundFires = new GroundFireSystem(particleSystem);
    // Optional MissionStats sink (combat scenarios) — kills are reported the
    // frame each entity first dies, keyed by its `params.model`.
    this.stats = null;

    // Takeoff grace: enemies spawn paused and stay dormant until the player has
    // travelled ENEMIES.activationDistance from their spawn point, then all
    // unpause together. `_playerSpawn` is captured on the first update tick that
    // has a player; `_enemiesPaused` latches false once the threshold is crossed.
    this._enemiesPaused = true;
    this._playerSpawn = null;
  }

  spawnPlane(type, spawn) {
    const params = PLANES[type];
    if (!params) {
      console.warn(`EnemyManager: unknown plane type "${type}"`);
      return null;
    }
    const model = this.assets.getModel(params.model);
    if (!model) {
      console.warn(`EnemyManager: model "${params.model}" not loaded`);
      return null;
    }

    const y = this._resolveY(spawn.x, spawn.y, spawn.z);
    const position = new THREE.Vector3(spawn.x, y, spawn.z);
    const heading = (spawn.heading || 0) * DEG2RAD;

    const plane = new EnemyPlane({
      model, params, position, heading, scene: this.scene,
      particleSystem: this.particleSystem,
      explosionSystem: this.explosionSystem,
      audioManager: this.audioManager,
      flareSystem: this.flareSystem,
      missileSystem: this.missileSystem,
      initialHealth: spawn.initialHealth,
      initialState: spawn.state || 'explore',
    });
    this.entities.push(plane);
    return plane;
  }

  spawnHelicopter(type, spawn) {
    const params = HELICOPTERS[type];
    if (!params) {
      console.warn(`EnemyManager: unknown helicopter type "${type}"`);
      return null;
    }
    const model = this.assets.getModel(params.model);
    if (!model) {
      console.warn(`EnemyManager: model "${params.model}" not loaded`);
      return null;
    }

    const y = this._resolveY(spawn.x, spawn.y, spawn.z);
    const position = new THREE.Vector3(spawn.x, y, spawn.z);
    const heading = (spawn.heading || 0) * DEG2RAD;

    const heli = new EnemyHelicopter({
      model, params, position, heading, scene: this.scene,
      particleSystem: this.particleSystem,
      explosionSystem: this.explosionSystem,
      audioManager: this.audioManager,
      flareSystem: this.flareSystem,
      initialHealth: spawn.initialHealth,
    });
    this.entities.push(heli);
    return heli;
  }

  spawnVehicle(type, spawn) {
    const params = VEHICLES[type];
    if (!params) {
      console.warn(`EnemyManager: unknown vehicle type "${type}"`);
      return null;
    }
    const model = this.assets.getModel(params.model);
    if (!model) {
      console.warn(`EnemyManager: model "${params.model}" not loaded`);
      return null;
    }

    const y = this._resolveY(spawn.x, spawn.y, spawn.z);
    const position = new THREE.Vector3(spawn.x, y, spawn.z);
    const heading = (spawn.heading || 0) * DEG2RAD;

    // AAA variants (2K22 Tunguska) route to a dedicated entity — stationary,
    // turret-armed, missile-equipped — instead of the drive-around APC.
    const vehicle = params.aaa
      ? new EnemyTunguska({
          model, params, position, heading, scene: this.scene,
          particleSystem: this.particleSystem,
          explosionSystem: this.explosionSystem,
          audioManager: this.audioManager,
          missileSystem: this.missileSystem,
          initialHealth: spawn.initialHealth,
        })
      : new EnemyGroundVehicle({
          model, params, position, heading, scene: this.scene,
          particleSystem: this.particleSystem,
          explosionSystem: this.explosionSystem,
          audioManager: this.audioManager,
          initialHealth: spawn.initialHealth,
          initialState: spawn.state || 'explore',
          roadNetwork: this.roadNetwork,
        });
    this.entities.push(vehicle);
    return vehicle;
  }

  // Generic trail-formation: leader at (x,y,z), N-1 followers staggered behind
  // along the leader's heading via the provided spawner + formation config.
  // Followers enter follow state targeting the entity directly ahead of them.
  _spawnTrailFormation(spawn, spawner, formationCfg, altitudeJitter = 0) {
    const count = Math.max(1, spawn.count | 0);
    const leader = spawner(spawn);
    if (!leader || count === 1) return leader;

    const headingRad = (spawn.heading || 0) * DEG2RAD;
    const backX =  Math.sin(headingRad);
    const backZ =  Math.cos(headingRad);
    const rightX =  Math.cos(headingRad);
    const rightZ = -Math.sin(headingRad);

    // Seed the straight line each vehicle is treated as having just driven in
    // along. Followers steer by breadcrumbs, so without this the leader's trail
    // begins at its own spawn point: a target one gap back along it lands a
    // full gap *ahead* of the follower already sitting there, and the column
    // settles at double spacing it has no speed reserve to close.
    this._primeStraightTrail(leader, backX, backZ);

    let prev = leader;
    for (let i = 1; i < count; i++) {
      const gap = formationCfg.spacing + srand() * formationCfg.spacingJitter;
      const lateral = srand() * formationCfg.lateralJitter;
      const altOffset = altitudeJitter ? srand() * altitudeJitter : 0;

      const baseX = prev.flight.position.x + backX * gap + rightX * lateral;
      const baseZ = prev.flight.position.z + backZ * gap + rightZ * lateral;
      const baseY = leader.flight.position.y + altOffset;

      const followerSpawn = {
        ...spawn,
        x: baseX,
        y: baseY,
        z: baseZ,
        count: undefined,
      };
      const follower = spawner(followerSpawn);
      if (!follower) break;

      this._primeStraightTrail(follower, backX, backZ);
      follower.pilot.followLeader(prev);
      prev = follower;
    }
    return leader;
  }

  // Seed a vehicle's breadcrumb trail with the straight line behind it, given
  // the unit vector pointing back along the formation's heading. No-op for
  // entities without a trail (aircraft pilots hold slots, not breadcrumbs).
  _primeStraightTrail(entity, backX, backZ) {
    if (typeof entity.pilot?.primeTrail !== 'function') return;

    const f = ENEMIES.vehicleFormation;
    const steps = Math.ceil(f.trailLength / f.trailSampleDist);
    const { x, z } = entity.flight.position;
    const pts = [];
    for (let i = steps; i >= 0; i--) {
      pts.push(x + backX * i * f.trailSampleDist, z + backZ * i * f.trailSampleDist);
    }
    entity.pilot.primeTrail(pts);
  }

  /**
   * Snap a vehicle column onto the road graph at its spawn position and start
   * it driving. The spawn keeps its ordinary placement — x/z, `spawnRadius`
   * jitter, player-relative resolution all apply first — and only then is the
   * result pulled to the nearest road, so `useRoads` changes where a formation
   * *drives*, not how it is positioned.
   *
   * Unlike _spawnTrailFormation, which staggers followers along a straight
   * heading, every vehicle is placed at its own arc length back down the road
   * being joined — through junctions if the column is longer than the edge — so
   * one spawning on a bend starts already curved onto it rather than straddling
   * it. Each also gets its trail primed with the road behind it, since
   * followers steer by breadcrumbs and a column with no history bunches up on
   * its first frame.
   *
   * Returns the leader, or null if the network is unusable.
   */
  _spawnRoadColumn(spawn) {
    const net = this.roadNetwork;
    if (!net?.ready) {
      console.warn('EnemyManager: `useRoads` spawn but no road network — placing normally');
      return null;
    }

    const near = net.nearest(spawn.x ?? 0, spawn.z ?? 0);
    if (!near) return null;
    // Allow for the jitter already applied: a spawn scattered over a disc can
    // legitimately land a full radius away from the road its centre sits on,
    // so a fixed threshold would cry wolf on every wide-disc entry.
    if (near.dist > ROADS.snapWarnDistance + (spawn.spawnRadius || 0)) {
      console.warn(
        `EnemyManager: \`useRoads\` spawn "${spawn.type}" snapped ${(near.dist / 1000).toFixed(1)} km `
        + 'to reach a road — check its x/z and spawnRadius against the network.',
      );
    }

    // Travel direction: whichever way along the road better matches the spawn's
    // own heading, so an authored heading still means something after the snap
    // and a formation doesn't flip end-for-end between launches. Which way it
    // goes at each *junction* stays random.
    net.sample(near.edge, near.s, _roadSample);
    const h = (spawn.heading || 0) * DEG2RAD;
    const dir = (-Math.sin(h) * _roadSample.tx + -Math.cos(h) * _roadSample.tz) >= 0 ? 1 : -1;

    const f = ENEMIES.vehicleFormation;
    const count = Math.max(1, spawn.count | 0);

    const gaps = [];
    let columnLength = 0;
    for (let i = 1; i < count; i++) {
      const gap = f.spacing + srand() * f.spacingJitter;
      gaps.push(gap);
      columnLength += gap;
    }

    // One committed walk back down the road, shared by every vehicle's position
    // and primed trail, so the whole column lies on a single continuous line.
    const behind = net.walkFrom(near.edge, -dir, near.s, columnLength + f.trailLength + 10);

    let leader = null;
    let prev = null;
    let offset = 0;

    for (let i = 0; i < count; i++) {
      if (i > 0) offset += gaps[i - 1];

      net.sampleRoute(behind, near.s, offset, _roadSample);
      // The walk runs backwards, so the vehicle faces against its tangent.
      const fwdX = -_roadSample.tx;
      const fwdZ = -_roadSample.tz;

      const vehicle = this.spawnVehicle(spawn.type, {
        ...spawn,
        x: _roadSample.x,
        z: _roadSample.z,
        y: 'agl:0',
        heading: Math.atan2(-fwdX, -fwdZ) * RAD2DEG,
        count: undefined,
      });
      if (!vehicle) break;

      vehicle.pilot.primeTrail(this._roadTrail(behind, near.s, offset));

      if (i === 0) {
        leader = vehicle;
        vehicle.pilot.enterPath({ edge: near.edge, dir, s: near.s });
      } else {
        vehicle.pilot.followLeader(prev);
      }
      prev = vehicle;
    }

    return leader;
  }

  /**
   * Park a stationary vehicle just off the nearest road, for `useRoads` spawns
   * that have no driver to follow one (AAA). Returns a rewritten spawn; the
   * original is returned unchanged if there's no usable network.
   *
   * Both sides of the road are tried and the one with more clearance kept:
   * where two roads run close together, a fixed side can drop the vehicle onto
   * the neighbouring carriageway, which is the one thing this is meant to avoid.
   */
  _placeRoadside(spawn) {
    const net = this.roadNetwork;
    if (!net?.ready) {
      console.warn('EnemyManager: `useRoads` spawn but no road network — placing normally');
      return spawn;
    }

    const near = net.nearest(spawn.x ?? 0, spawn.z ?? 0);
    if (!near) return spawn;
    if (near.dist > ROADS.snapWarnDistance + (spawn.spawnRadius || 0)) {
      console.warn(
        `EnemyManager: \`useRoads\` spawn "${spawn.type}" snapped ${(near.dist / 1000).toFixed(1)} km `
        + 'to reach a road — check its x/z and spawnRadius against the network.',
      );
    }

    net.sample(near.edge, near.s, _roadSample);
    const o = ROADS.roadsideOffset;
    // Right of a forward (tx, tz) is (-tz, tx) under this sim's convention.
    const perpX = -_roadSample.tz;
    const perpZ =  _roadSample.tx;

    // Several offsets on both sides, keeping whichever ends up with the most
    // room. One fixed offset is not enough: where two roads run within the
    // offset distance of each other, stepping off one lands on the other, and
    // both sides can be blocked at the distance first tried.
    let bestX = _roadSample.x + perpX * o.max;
    let bestZ = _roadSample.z + perpZ * o.max;
    let bestClear = -1;

    for (let i = 0; i < ROADSIDE_ATTEMPTS; i++) {
      const dist = o.min + Math.random() * (o.max - o.min);
      for (const side of [1, -1]) {
        const x = _roadSample.x + perpX * dist * side;
        const z = _roadSample.z + perpZ * dist * side;
        const clear = net.nearest(x, z)?.dist ?? 0;
        if (clear > bestClear) {
          bestClear = clear;
          bestX = x;
          bestZ = z;
        }
      }
    }

    // The placement moved, so any absolute height the entry carried is stale.
    return { ...spawn, x: bestX, z: bestZ, y: 'agl:0' };
  }

  // Sample the road behind a vehicle into a flat [x, z, …] trail, oldest first
  // — the history it would have laid down driving in. `offset` is how far back
  // down `behind` the vehicle itself sits.
  _roadTrail(behind, startS, offset) {
    const f = ENEMIES.vehicleFormation;
    const steps = Math.ceil(f.trailLength / f.trailSampleDist);
    const pts = [];
    for (let i = steps; i >= 0; i--) {
      this.roadNetwork.sampleRoute(behind, startS, offset + i * f.trailSampleDist, _roadSample);
      pts.push(_roadSample.x, _roadSample.z);
    }
    return pts;
  }

  spawnForScenario(scenario, player = null) {
    const list = scenario?.spawns;
    if (!list) return;
    for (const rawSpawn of list) this.spawnEntry(rawSpawn, player);
  }

  /**
   * Place one `spawns`-shaped entry (see SCENARIOS in SimConfig for the field
   * list). Split out of {@link spawnForScenario} because a training mission
   * reveals its targets a phase at a time rather than all at launch, so unlike
   * the scenario load this also runs mid-flight — which is what the pause-state
   * sync at the bottom is for.
   */
  spawnEntry(rawSpawn, player = null) {
    const spawn = this._applySpawnRadius(this._resolvePlayerRelative(rawSpawn, player));
    // Everything appended below this mark came from this entry, so a spawn
    // that names itself (`id`) tags its whole formation — that's what a
    // training mission's "destroy target" objective checks against.
    const firstNew = this.entities.length;
    if (spawn.type in HELICOPTERS) {
      if ((spawn.count | 0) > 1) {
        this._spawnTrailFormation(
          spawn,
          (s) => this.spawnHelicopter(spawn.type, s),
          ENEMIES.formation,
          ENEMIES.formation.altitudeJitter,
        );
      } else {
        this.spawnHelicopter(spawn.type, spawn);
      }
    } else if (spawn.type in PLANES) {
      this.spawnPlane(spawn.type, spawn);
    } else if (spawn.type in VEHICLES) {
      // AAA vehicles are stationary and have no driver, so they can't head or
      // tail a column — always single-spawn regardless of `count`.
      const aaa = !!VEHICLES[spawn.type].aaa;

      // `useRoads` means two different things either side of that. A driving
      // column keeps its ordinary placement and is then snapped onto the
      // nearest road to follow it, instead of wandering cross-country from
      // wherever it landed. A stationary piece has nothing to follow, so it
      // is emplaced just off the roadside instead — see _placeRoadside.
      const placed = (spawn.useRoads && aaa) ? this._placeRoadside(spawn) : spawn;

      // A null return means the network is missing or empty — fall through to
      // ordinary placement, so a failed bake degrades to the old behaviour
      // rather than spawning nothing.
      const useRoads = spawn.useRoads && !aaa && this._spawnRoadColumn(spawn);

      if (useRoads) {
        // Placed above.
      } else if ((spawn.count | 0) > 1 && !aaa) {
        this._spawnTrailFormation(
          spawn,
          (s) => this.spawnVehicle(spawn.type, s),
          ENEMIES.vehicleFormation,
          0,
        );
      } else {
        this.spawnVehicle(spawn.type, placed);
      }
    }
    for (let i = firstNew; i < this.entities.length; i++) {
      const e = this.entities[i];
      // Entities are born dormant for the takeoff grace. _updatePauseState is
      // latched, so one placed after the grace has already lifted would never
      // be woken by it — adopt the current state instead of the constructor's.
      e.paused = this._enemiesPaused;
      if (spawn.id) e.trainingId = spawn.id;
    }
  }

  update(dt, player = null) {
    this._updatePauseState(player);
    for (const e of this.entities) e.update(dt, this.terrainCollider, player);

    // Score each kill once, the frame an entity first becomes doomed. Aircraft
    // (heli/plane) trip `flight.structuralFailure` at the kill; ground vehicles
    // and the Tunguska latch `destroyed`. `dead` (sunk wreck) is a late backstop.
    if (this.stats) {
      for (const e of this.entities) {
        if (e._killScored) continue;
        if (e.destroyed || e.dead || e.flight?.structuralFailure) {
          e._killScored = true;
          this.stats.recordEnemyKill(e.params?.model);
        }
      }
    }

    // Prune entities that despawned this frame (e.g. wreckage below terrain).
    // When a leader is pruned, promote the next-in-line follower (the bird
    // that was tailing it) to head its own chain by reverting to explore.
    // Destroyed ground vehicles never set `dead` so their wrecks stay in
    // place; their followers detect `destroyed` and promote themselves.
    for (let i = this.entities.length - 1; i >= 0; i--) {
      const dying = this.entities[i];
      if (!dying.dead) continue;
      if (dying.crashSite) {
        const c = dying.crashSite;
        this.groundFires.add(c.x, c.y, c.z);
      }
      for (const other of this.entities) {
        if (other.pilot?.leader === dying) other.pilot.promoteToLeader();
      }
      dying.dispose();
      this.entities.splice(i, 1);
    }

    this.groundFires.update(dt);
  }

  // Takeoff grace: hold every enemy paused until the player has moved
  // ENEMIES.activationDistance from where they spawned, then release them all
  // at once. Latched, so it only runs until the threshold is first crossed.
  _updatePauseState(player) {
    if (!this._enemiesPaused || !player) return;
    if (this._playerSpawn === null) {
      this._playerSpawn = player.position.clone();
      return;
    }
    const d = ENEMIES.activationDistance;
    if (player.position.distanceToSquared(this._playerSpawn) >= d * d) {
      this._enemiesPaused = false;
      for (const e of this.entities) e.paused = false;
    }
  }

  // Number of enemies still alive. Helicopters/planes set `dead` and are pruned
  // out of `entities` on death; ground vehicles leave a wreck in `entities` but
  // flag `destroyed`. Counting the not-`destroyed` survivors handles both.
  aliveCount() {
    let n = 0;
    for (const e of this.entities) if (!e.destroyed) n++;
    return n;
  }

  dispose() {
    for (const e of this.entities) e.dispose();
    this.entities.length = 0;
    this.groundFires.dispose();
  }

  // Resolve a `relativeTo: 'player'` spawn against the player's launch pose,
  // rewriting it into the absolute form the rest of the pipeline expects.
  // `ahead`/`right` are metres along/right of the player's ground track (pitch
  // ignored, so "ahead" is level); `y: 'player'` matches the player's altitude
  // and `heading: 'player'` its ground heading. Any of those may still be given
  // in absolute form. Combat spawn poses are jittered per launch and the player
  // may pick a runway/parking start, so this is the only way to place an enemy
  // at a fixed offset from wherever the player actually ends up.
  _resolvePlayerRelative(spawn, player) {
    if (spawn?.relativeTo !== 'player') return spawn;
    if (!player) {
      console.warn('EnemyManager: player-relative spawn with no player — using raw x/y/z');
      return spawn;
    }

    const fwd = player.forwardVector;
    // Ground heading (rad): forward at heading h is (-sin h, ·, -cos h).
    const h = Math.atan2(-fwd.x, -fwd.z);
    const ahead = spawn.ahead || 0;
    const right = spawn.right || 0;

    return {
      ...spawn,
      x: player.position.x + -Math.sin(h) * ahead + Math.cos(h) * right,
      z: player.position.z + -Math.cos(h) * ahead + -Math.sin(h) * right,
      y: spawn.y === 'player' ? player.position.y : spawn.y,
      heading: spawn.heading === 'player' ? h * RAD2DEG : spawn.heading,
    };
  }

  // Apply an optional `spawnRadius` (metres): nudge the spawn's XZ origin to a
  // random point within that disc, re-rolled every scenario start. Returns the
  // spawn unchanged when no radius is set. The offset is applied once per
  // top-level spawn entry, so formations (count > 1) relocate as a unit — the
  // leader moves and followers, placed relative to it, keep their geometry.
  // 'agl:N' heights re-resolve at the new XZ; absolute numeric y is untouched.
  _applySpawnRadius(spawn) {
    const radius = spawn?.spawnRadius;
    if (!(radius > 0)) return spawn;
    // Uniform over the disc area (sqrt keeps points from clustering at centre).
    const r = radius * Math.sqrt(Math.random());
    const theta = Math.random() * Math.PI * 2;
    return {
      ...spawn,
      x: spawn.x + r * Math.cos(theta),
      z: spawn.z + r * Math.sin(theta),
    };
  }

  // Resolve a spawn 'y' field. Numeric → absolute Y. String 'agl:N' → N
  // metres above local terrain (falls back to N if heightmap not yet loaded).
  _resolveY(x, y, z) {
    if (typeof y === 'number') return y;
    if (typeof y === 'string' && y.startsWith('agl:')) {
      const offset = parseFloat(y.slice(4));
      const ground = this.terrainCollider?.getHeightAt(x, z);
      return (ground ?? 0) + offset;
    }
    return 0;
  }
}
